import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AvailabilityMatrix, AvailabilityPicker } from '../components/AvailabilityPicker'
import { IllustrationSlot } from '../components/IllustrationSlot'
import { RESORTS, resortById } from '../data/resorts'
import { availabilityWindow, eachDay } from '../lib/dates'
import { encodePayload } from '../lib/share'
import { useStore } from '../lib/state'

export function Trips() {
  const { state, createTrip, deleteTrip, setProfile, updateTrip } = useStore()
  const [resortId, setResortId] = useState('alta')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [note, setNote] = useState('')
  const [hostAvail, setHostAvail] = useState<string[]>([])
  const [copied, setCopied] = useState<string | null>(null)

  const windowDays = useMemo(
    () => availabilityWindow(startDate, endDate, 2),
    [startDate, endDate],
  )

  const onCreate = () => {
    if (!resortId || !startDate || !endDate) return
    if (!state.profile.displayName) setProfile({ displayName: 'Skier' })
    const trip = createTrip({
      resortId,
      startDate,
      endDate,
      hostName: state.profile.displayName || 'Skier',
      note: note || undefined,
      hostAvailability: hostAvail.length ? hostAvail : eachDay(startDate, endDate),
    })
    const payload = {
      v: 1 as const,
      id: trip.id,
      resortId: trip.resortId,
      startDate: trip.startDate,
      endDate: trip.endDate,
      hostName: trip.hostName,
      note: trip.note,
      hostAvailability: trip.hostAvailability,
    }
    const url = `${window.location.origin}/trip/${trip.id}?d=${encodePayload(payload)}`
    navigator.clipboard?.writeText(url).catch(() => {})
    setCopied(trip.id)
    setTimeout(() => setCopied(null), 2500)
    setNote('')
    setHostAvail([])
  }

  return (
    <div className="stack">
      <div className="card stack create-panel">
        <div className="row between" style={{ alignItems: 'flex-start' }}>
          <div className="page-head">
            <h2>Create a trip</h2>
            <p className="muted">
              Soft-publish a mountain + dates. Friends set availability and tap Join — no account needed.
            </p>
          </div>
          <IllustrationSlot kind="trip" caption="art later" />
        </div>
        <div className="field">
          <label>Host name on the card</label>
          <input
            value={state.profile.displayName}
            placeholder="Your name"
            onChange={(e) => setProfile({ displayName: e.target.value })}
          />
        </div>
        <div className="row" style={{ alignItems: 'end' }}>
          <div className="field" style={{ flex: 1, minWidth: 160 }}>
            <label>Mountain</label>
            <select value={resortId} onChange={(e) => setResortId(e.target.value)}>
              {RESORTS.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Start</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value)
                if (e.target.value && endDate) {
                  setHostAvail(eachDay(e.target.value, endDate))
                }
              }}
            />
          </div>
          <div className="field">
            <label>End</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value)
                if (startDate && e.target.value) {
                  setHostAvail(eachDay(startDate, e.target.value))
                }
              }}
            />
          </div>
        </div>
        <div className="field">
          <label>Note (optional)</label>
          <input
            value={note}
            placeholder="Powder chase · lodging TBD"
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <AvailabilityPicker
          days={windowDays}
          selected={hostAvail}
          onChange={setHostAvail}
          tripStart={startDate}
          tripEnd={endDate}
          label="Your availability"
        />

        <button className="btn primary lg" onClick={onCreate} disabled={!resortId || !startDate || !endDate}>
          Create trip & copy link
        </button>
      </div>

      <div className="card stack">
        <h3 style={{ margin: 0 }}>Your trips</h3>
        {state.trips.length === 0 ? (
          <div className="empty">No trips yet — create one above to invite your crew.</div>
        ) : (
          <div className="list">
            {state.trips.map((t) => {
              const days = availabilityWindow(t.startDate, t.endDate, 2)
              const payload = {
                v: 1 as const,
                id: t.id,
                resortId: t.resortId,
                startDate: t.startDate,
                endDate: t.endDate,
                hostName: t.hostName,
                note: t.note,
                hostAvailability: t.hostAvailability,
              }
              const url = `${typeof window !== 'undefined' ? window.location.origin : ''}/trip/${t.id}?d=${encodePayload(payload)}`
              const matrixRows = [
                { name: `${t.hostName} (host)`, dates: t.hostAvailability ?? [], highlight: true },
                ...t.rsvps.map((r) => ({ name: r.name, dates: r.availableDates ?? [] })),
              ]
              return (
                <div className="list-item" key={t.id} style={{ alignItems: 'flex-start', flexDirection: 'column' }}>
                  <div className="stack" style={{ gap: 10, width: '100%' }}>
                    <div style={{ fontWeight: 750, fontSize: 16 }}>{resortById(t.resortId)?.name}</div>
                    <div className="muted" style={{ fontSize: 13 }}>
                      {t.startDate} → {t.endDate} · host {t.hostName}
                    </div>
                    {t.note && (
                      <div className="faint" style={{ fontSize: 12 }}>
                        {t.note}
                      </div>
                    )}
                    <div className="row">
                      <span className="chip indy">
                        {t.rsvps.filter((r) => r.status === 'join').length} joining
                      </span>
                      <span className="chip">
                        {t.rsvps.filter((r) => r.status === 'maybe').length} maybe
                      </span>
                      <span className="chip">
                        {(t.hostAvailability ?? []).length} host days free
                      </span>
                    </div>

                    <AvailabilityPicker
                      days={days}
                      selected={t.hostAvailability ?? []}
                      onChange={(next) => updateTrip(t.id, { hostAvailability: next })}
                      tripStart={t.startDate}
                      tripEnd={t.endDate}
                      label="Update your availability"
                    />

                    {(t.rsvps.length > 0 || (t.hostAvailability ?? []).length > 0) && (
                      <AvailabilityMatrix days={days} rows={matrixRows} />
                    )}

                    <div className="row">
                      <Link className="btn sm primary" to={`/trip/${t.id}?d=${encodePayload(payload)}`}>
                        Open card
                      </Link>
                      <button
                        className="btn sm"
                        onClick={() => {
                          navigator.clipboard?.writeText(url)
                          setCopied(t.id)
                          setTimeout(() => setCopied(null), 2000)
                        }}
                      >
                        {copied === t.id ? 'Copied' : 'Copy link'}
                      </button>
                      <button className="btn sm danger" onClick={() => deleteTrip(t.id)}>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
      {copied && <div className="toast">Link copied · share with your crew</div>}
    </div>
  )
}
